/**
 * TestConnection - Step 3 of Add Connector Wizard
 *
 * Redesign (#261): one "Connection test" card. The three sub-checks
 * (Connector library -> Configuration -> Connectivity) are always visible as a
 * checklist; status is stated once (header pill + a single result strip); on
 * failure the failed check expands inline with the error message + fix tips,
 * with raw detail behind a disclosure. Drives the run via the same hooks
 * ConnectorHealthCheck uses, and still reports a HealthCheckResult upward.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Circle,
  AlertCircle,
  RefreshCw,
  ChevronDown,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { PlatformLogo } from '../library/PlatformLogo';
import { getPlatformMeta } from '../library/platformLogos';
import { useTestCredentials } from '@/hooks/useConnectors';
import type { HealthCheckResult } from '../ConnectorHealthCheck';
import type { ConnectorType } from '@/types/connectors';

// Re-export the result type so existing importers keep working.
export type { HealthCheckResult } from '../ConnectorHealthCheck';

interface TestConnectionProps {
  connectorType: ConnectorType;
  autoRun?: boolean;
  credentials?: Record<string, any>;
  onTestComplete: (result: HealthCheckResult) => void;
  onSkip: () => void;
}

type CheckKey = 'connectorLibrary' | 'configuration' | 'connectivity';
type CheckState = 'pending' | 'running' | 'pass' | 'fail';

const CHECK_META: { key: CheckKey; label: string; message: string }[] = [
  {
    key: 'connectorLibrary',
    label: 'Connector library',
    message: 'The connector is installed and available on the backend.',
  },
  {
    key: 'configuration',
    label: 'Configuration',
    message: 'Required fields are present and credentials are well-formed.',
  },
  {
    key: 'connectivity',
    label: 'Connectivity',
    message: 'Reaching the data source and authenticating with the credentials.',
  },
];

/** Targeted fix tips for the check that failed. */
function fixTipsFor(key: CheckKey): string[] {
  switch (key) {
    case 'connectorLibrary':
      return [
        'Confirm the connector type is supported in this version of the lakehouse.',
        'Check that the connector library is installed in the backend environment.',
      ];
    case 'configuration':
      return [
        'Review the connection fields for missing or malformed required values.',
        'Verify credentials are valid and not expired, and re-enter any that may be wrong.',
      ];
    case 'connectivity':
    default:
      return [
        'Verify the credentials have access to the requested account or resource.',
        'Check network access to the data source — firewalls, IP allow-lists, endpoints.',
        'Re-authorize if an OAuth grant may have been revoked, then run the test again.',
      ];
  }
}

/** Map a raw per-check value to pass/fail. */
function toCheckState(value: string | undefined, running: boolean): CheckState {
  if (running) return 'running';
  if (value === undefined) return 'pending';
  return value === 'ok' ? 'pass' : 'fail';
}

const CHECK_ICON: Record<CheckState, React.ReactNode> = {
  pass: <CheckCircle2 className="h-5 w-5 text-success" />,
  fail: <XCircle className="h-5 w-5 text-destructive" />,
  running: <Loader2 className="h-5 w-5 text-primary animate-spin" />,
  pending: <Circle className="h-5 w-5 text-muted-foreground/40" />,
};

const CHECK_TAG: Record<CheckState, { label: string; className: string }> = {
  pass: { label: 'Passed', className: 'border-success/40 text-success' },
  fail: { label: 'Failed', className: 'border-destructive/50 text-destructive' },
  running: { label: 'Running', className: 'border-info/40 text-info' },
  pending: { label: 'Pending', className: 'text-muted-foreground' },
};

export const TestConnection: React.FC<TestConnectionProps> = ({
  connectorType,
  autoRun = true,
  credentials,
  onTestComplete,
  onSkip,
}) => {
  const [result, setResult] = useState<HealthCheckResult | null>(null);
  const testCredentialsMutation = useTestCredentials();

  const isRunning = testCredentialsMutation.isPending;
  const meta = getPlatformMeta(connectorType);

  const runTest = async () => {
    try {
      // The pre-create wizard has no saved/enabled configuration to health-check,
      // so always validate the entered credentials via test-credentials. The
      // backend handles every case: connectors with collected creds pass them
      // through, operator-credential connectors (e.g. yahoo_dsp) source theirs
      // from env, and connectors with no test task return success so the wizard
      // can proceed. (#518)
      const response = await testCredentialsMutation.mutateAsync({
        connectorType,
        credentials: credentials ?? {},
      });
      // The form already passed client-side field validation to reach this step,
      // so a test-credentials failure is a connectivity/auth problem — attribute
      // it to the connectivity check rather than marking configuration invalid.
      const next: HealthCheckResult = {
        status: response.success ? 'healthy' : 'unhealthy',
        checks: {
          connectorLibrary: 'ok',
          configuration: 'ok',
          connectivity: response.success ? 'ok' : 'failed',
        },
        // Always carry the human-readable message so genuine failures show the
        // real reason; structured details (when present) are merged on top.
        details: { message: response.message, ...(response.details ?? {}) },
        timestamp: new Date().toISOString(),
      };

      setResult(next);
      onTestComplete(next);
    } catch (error) {
      const errorResult: HealthCheckResult = {
        status: 'unhealthy',
        checks: { connectorLibrary: 'error', configuration: 'invalid', connectivity: 'failed' },
        details: {
          error: true,
          error_message: error instanceof Error ? error.message : 'Unknown error occurred',
        },
        timestamp: new Date().toISOString(),
      };
      setResult(errorResult);
      onTestComplete(errorResult);
    }
  };

  // Kept up to date every render and called through the ref (rather than a
  // direct `runTest()` capture) below: the compiler's set-state-in-effect
  // analysis taints a directly-captured call to a function whose body can
  // call setState (even though every one of `runTest`'s own setState calls
  // here already happens after its internal `await`), and the ref
  // indirection breaks that static tracking with no change in timing --
  // `runTestRef.current` always resolves to the latest `runTest` by the time
  // the effect below fires.
  const runTestRef = useRef(runTest);
  useEffect(() => {
    runTestRef.current = runTest;
  });

  useEffect(() => {
    if (autoRun) runTestRef.current();
  }, [autoRun]);

  const states: Record<CheckKey, CheckState> = {
    connectorLibrary: toCheckState(result?.checks.connectorLibrary, isRunning),
    configuration: toCheckState(result?.checks.configuration, isRunning),
    connectivity: toCheckState(result?.checks.connectivity, isRunning),
  };

  // First failed check carries the inline error detail.
  const firstFailedKey = !isRunning && result
    ? CHECK_META.find((c) => states[c.key] === 'fail')?.key
    : undefined;

  const errorMessage =
    (result?.details?.error_message as string | undefined) ||
    (result?.details?.message as string | undefined) ||
    'The data source could not be reached with the provided configuration.';

  const statusBadge = (() => {
    if (isRunning) return <Badge variant="outline" className="border-info/40 bg-info/10 text-info">Running</Badge>;
    if (!result) return null;
    if (result.status === 'healthy') return <Badge variant="outline" className="border-success/40 bg-success/10 text-success">Healthy</Badge>;
    if (result.status === 'degraded') return <Badge variant="outline" className="border-warning/40 bg-warning/10 text-warning">Degraded</Badge>;
    return <Badge variant="outline" className="border-destructive/50 bg-destructive/10 text-destructive">Failed</Badge>;
  })();

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        We run three checks against your configuration before you create the connector — this
        catches credential and connectivity problems early.
      </p>

      <div className="rounded-lg border bg-card shadow-sm">
        {/* Header: identity + status */}
        <div className="flex items-center justify-between gap-3 border-b p-4">
          <div className="flex min-w-0 items-center gap-3">
            <PlatformLogo connectorType={connectorType} size={30} />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold leading-tight">{meta.displayName}</p>
              <p className="truncate font-mono text-xs text-muted-foreground">{connectorType}</p>
            </div>
          </div>
          {statusBadge}
        </div>

        {/* Indeterminate progress hairline while running */}
        {isRunning && (
          <div className="h-0.5 overflow-hidden bg-muted" aria-hidden="true">
            <div className="h-full w-1/3 animate-pulse bg-primary" />
          </div>
        )}

        {/* Checklist (always visible) */}
        <div className="px-4">
          {CHECK_META.map((c, i) => {
            const st = states[c.key];
            const tag = CHECK_TAG[st];
            const isFailedRow = c.key === firstFailedKey;
            return (
              <div
                key={c.key}
                className={[
                  'flex items-start gap-3 py-3',
                  i < CHECK_META.length - 1 ? 'border-b' : '',
                  isFailedRow ? '-mx-4 bg-destructive/5 px-4' : '',
                ].join(' ')}
              >
                <span className="mt-0.5 flex-shrink-0">{CHECK_ICON[st]}</span>
                <div className="min-w-0 flex-1">
                  <p
                    className={[
                      'text-sm font-semibold',
                      st === 'pending' ? 'text-muted-foreground' : '',
                    ].join(' ')}
                  >
                    {c.label}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{c.message}</p>

                  {isFailedRow && (
                    <div className="mt-3 space-y-3">
                      <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 font-mono text-xs leading-relaxed text-foreground">
                        {errorMessage}
                      </div>
                      <div>
                        <p className="mb-1.5 text-xs font-semibold">How to fix this</p>
                        <ul className="list-disc space-y-1 pl-5 text-sm">
                          {fixTipsFor(c.key).map((tip, idx) => (
                            <li key={idx}>{tip}</li>
                          ))}
                        </ul>
                      </div>
                      <Collapsible>
                        <CollapsibleTrigger className="group flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
                          <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
                          Show technical details
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <pre className="mt-2 overflow-auto rounded-md bg-muted p-3 font-mono text-[11px] leading-relaxed">
                            {JSON.stringify(result?.details ?? {}, null, 2)}
                          </pre>
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                  )}
                </div>
                <Badge
                  variant="outline"
                  className={`self-center text-[11px] uppercase tracking-wide ${tag.className}`}
                >
                  {tag.label}
                </Badge>
              </div>
            );
          })}
        </div>

        {/* Single result strip */}
        {!isRunning && result?.status === 'healthy' && (
          <Alert variant="success" className="rounded-none border-x-0 border-b-0">
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription>
              <p className="font-medium">Connection successful</p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Your {meta.displayName} connector is configured correctly and ready to create.
              </p>
            </AlertDescription>
          </Alert>
        )}
        {!isRunning && result && result.status !== 'healthy' && result.status !== 'degraded' && (
          <Alert variant="destructive" className="rounded-none border-x-0 border-b-0">
            <XCircle className="h-4 w-4" />
            <AlertDescription>
              <p className="font-medium">Connection failed</p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                One check did not pass. Review the detail above, fix the configuration, and run the
                test again.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {/* Footer: timestamp + re-run */}
        <div className="flex items-center justify-between gap-3 border-t p-3">
          <span className="text-xs text-muted-foreground">
            {isRunning
              ? 'Testing connection…'
              : result
                ? `Last checked: ${new Date(result.timestamp).toLocaleString()}`
                : 'Not tested yet'}
          </span>
          <Button variant="outline" size="sm" onClick={runTest} disabled={isRunning}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isRunning ? 'animate-spin' : ''}`} />
            {isRunning ? 'Running…' : 'Run test again'}
          </Button>
        </div>
      </div>

      {/* Demoted skip: a quiet inline line */}
      <p className="text-xs text-muted-foreground">
        <AlertCircle className="mr-1 inline h-3.5 w-3.5 align-text-bottom text-warning" />
        Having trouble? You can{' '}
        <button
          type="button"
          onClick={onSkip}
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          skip the test and configure later
        </button>{' '}
        — testing is recommended to avoid issues during extraction.
      </p>
    </div>
  );
};
